import type { DocumentMeta, Rect, RGBA, ShapeLayer, ShapePaint } from '../core/types';
import { geometryToSubpaths } from '../vector/geometry';
import { rasterizeCoverage, rasterizeShape, type FillStyle } from '../vector/rasterize';
import type { ShapeStroke, Subpath } from '../vector/types';
import type { Engine } from './engine';
import { layerMaskOp, pixelOp, type EngineHistoryOp, type FullSnapshot } from './historyOps';
import { applyToLayer } from './ops';

function fullRect(doc: DocumentMeta): Rect {
  return { x: 0, y: 0, width: doc.width, height: doc.height };
}

function fillStyle(fill: ShapePaint): FillStyle | null {
  if (fill.kind === 'none') return null;
  if (fill.kind === 'solid') return { kind: 'solid', color: fill.color };
  return { kind: 'gradient', gradient: fill.gradient, angle: fill.angle };
}

/** Flattened editable geometry of a shape layer. */
export function shapeSubpaths(layer: ShapeLayer): Subpath[] {
  return geometryToSubpaths(layer.geometry);
}

/**
 * Rasterises a shape layer's geometry into its surface. Called on every geometry,
 * fill or stroke change, and after a transform, so the shape is always drawn at
 * the document's resolution instead of being resampled.
 *
 * `record` is off while a shape is being dragged: a full-surface snapshot pair
 * per pointer move would cost megabytes for nothing, since only the committed
 * state belongs in history.
 */
export function renderShapeLayer(
  engine: Engine,
  doc: DocumentMeta,
  layer: ShapeLayer,
  record = true,
): EngineHistoryOp[] {
  const surface = engine.resourcesFor(doc).ensureLayer(layer.id).surface;
  const rect = fullRect(doc);
  const before = record ? surface.readPixels(rect) : null;

  const image = rasterizeShape(shapeSubpaths(layer), doc.width, doc.height, {
    fill: fillStyle(layer.fill),
    stroke: layer.stroke,
  });
  if (image) surface.writePixels(rect, new Uint8Array(image.data.buffer));
  else surface.target.clear(0, 0, 0, 0);

  if (!before) return [];
  return [pixelOp(layer.id, { rect, data: before }, { rect, data: surface.readPixels(rect) })];
}

function maskSnapshot(engine: Engine, doc: DocumentMeta, layerId: string): FullSnapshot | null {
  const mask = engine.resourcesFor(doc).layer(layerId)?.mask;
  if (!mask) return null;
  return { width: mask.width, height: mask.height, data: mask.texture.readPixels() };
}

/**
 * Rasterises a vector mask into the layer's mask texture. A vector mask shares
 * the raster mask's storage, so everything downstream — the compositor, Apply
 * Mask, painting into the mask — keeps working unchanged.
 */
export function renderVectorMask(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  subpaths: readonly Subpath[],
  record = true,
): EngineHistoryOp[] {
  const before = record ? maskSnapshot(engine, doc, layerId) : null;
  const mask = engine.resourcesFor(doc).ensureMask(layerId);
  const coverage = rasterizeCoverage(subpaths, doc.width, doc.height);
  if (coverage) mask.texture.uploadPixels(fullRect(doc), coverage);
  else mask.clear(0, 0, 0, 0);

  if (!record) return [];
  return [layerMaskOp(layerId, before, maskSnapshot(engine, doc, layerId))];
}

/** Coverage of a path, for Path to Selection. */
export function pathCoverage(doc: DocumentMeta, subpaths: readonly Subpath[]): Uint8Array | null {
  return rasterizeCoverage(subpaths, doc.width, doc.height);
}

/**
 * Paints a path's coverage into a layer, the pixel half of the Pen tool's Pixels
 * mode and of the Paths panel's Fill Path and Stroke Path. `stroke` draws the
 * outline instead of the interior.
 */
export function fillPathIntoLayer(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  subpaths: readonly Subpath[],
  options: { color: RGBA; opacity: number; stroke: (ShapeStroke & { color: RGBA }) | null },
): EngineHistoryOp[] | null {
  const image = rasterizeShape(subpaths, doc.width, doc.height, {
    fill: options.stroke ? null : { kind: 'solid', color: options.color },
    stroke: options.stroke,
  });
  if (!image) return null;

  const rect = fullRect(doc);
  const resources = engine.resourcesFor(doc);
  const coverage = resources.r8(1);
  const alpha = new Uint8Array(doc.width * doc.height);
  for (let i = 0; i < alpha.length; i += 1) alpha[i] = image.data[i * 4 + 3];
  coverage.texture.uploadPixels(rect, alpha);

  return applyToLayer(engine, doc, layerId, rect, (target, layerTexture) => {
    engine.renderer.applyStroke(
      target.framebuffer,
      layerTexture,
      coverage.texture,
      resources.selection.activeTexture,
      options.stroke ? options.stroke.color : options.color,
      options.opacity,
      false,
    );
  });
}
